import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, FileText, ShieldCheck, ChartBar, 
  MagnifyingGlass, CaretDown, CaretRight,
  Download, ArrowRight, Gauge, Info,
  Coins, Gavel, Brain, Bank, UsersFour, Lock, Handshake, Leaf, Buildings,
  Database, FilePdf, FileXls, PencilSimple, X, Check, Warning,
  ChartPie, ArrowsClockwise, Tag, Folder
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Icon mapping for dynamic themes
const ICON_MAP = {
  Coins, Gavel, Brain, Bank, UsersFour, Handshake, ShieldCheck, Lock, Globe,
  Database, Leaf, Buildings, Folder, FileText, Tag, ChartPie
};

// Instrument type labels
const INSTRUMENT_TYPES = {
  act: { label: 'Act', color: 'bg-purple-500/20 text-purple-400' },
  regulation: { label: 'Regulation', color: 'bg-blue-500/20 text-blue-400' },
  directive: { label: 'Directive', color: 'bg-amber-500/20 text-amber-400' },
  circular: { label: 'Circular', color: 'bg-cyan-500/20 text-cyan-400' },
  guidance: { label: 'Guidance Note', color: 'bg-emerald-500/20 text-emerald-400' },
  notice: { label: 'Notice', color: 'bg-pink-500/20 text-pink-400' },
  standard: { label: 'Standard', color: 'bg-indigo-500/20 text-indigo-400' },
  other: { label: 'Other', color: 'bg-gray-500/20 text-gray-400' }
};

// Risk level colors
const getRiskLevelStyle = (riskLevel) => {
  const styles = {
    Critical: { bg: 'bg-red-900/30', border: 'border-red-500', text: 'text-red-400', badge: 'bg-red-500' },
    High: { bg: 'bg-red-500/20', border: 'border-red-400', text: 'text-red-400', badge: 'bg-red-500' },
    Medium: { bg: 'bg-amber-500/20', border: 'border-amber-400', text: 'text-amber-400', badge: 'bg-amber-500' },
    Low: { bg: 'bg-emerald-500/20', border: 'border-emerald-400', text: 'text-emerald-400', badge: 'bg-emerald-500' }
  };
  return styles[riskLevel] || styles.Medium;
};

const UniverseRiskDashboard = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [themeRisks, setThemeRisks] = useState([]);
  const [expandedThemes, setExpandedThemes] = useState(new Set());
  const [methodology, setMethodology] = useState(null);
  const [usingMethodology, setUsingMethodology] = useState('aura_default');
  const [showEditRiskModal, setShowEditRiskModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [stats, setStats] = useState({ totalThemes: 0, totalInstruments: 0, avgRisk: 0, criticalCount: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRiskLevel, setFilterRiskLevel] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch theme risks and instruments
      const [risksRes, methodologyRes] = await Promise.all([
        axios.get(`${API}/regulatory-universe/theme-risks`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/company-profile/methodologies`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setThemeRisks(risksRes.data.theme_risks || []);
      setStats(risksRes.data.stats || {});
      setUsingMethodology(methodologyRes.data.using_methodology || 'aura_default');
      
      // Find active methodology if custom
      if (methodologyRes.data.using_methodology === 'custom') {
        const activeMeth = methodologyRes.data.methodologies?.find(m => m.is_active);
        setMethodology(activeMeth);
      }
    } catch (error) {
      console.error('Failed to load universe data:', error);
      toast.error('Failed to load Regulatory Universe data');
    } finally {
      setLoading(false);
    }
  };

  const toggleThemeExpand = (themeId) => {
    setExpandedThemes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(themeId)) {
        newSet.delete(themeId);
      } else {
        newSet.add(themeId);
      }
      return newSet;
    });
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/regulatory-universe/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regulatory_universe_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel export downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await axios.get(`${API}/regulatory-universe/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regulatory_universe_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF export downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const filteredThemeRisks = themeRisks.filter(theme => {
    const matchesSearch = !searchTerm || 
      theme.theme_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      theme.instruments?.some(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterRiskLevel === 'all' || theme.risk_level === filterRiskLevel;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-gold font-mono">Loading Regulatory Universe...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1 p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-medium text-text-primary flex items-center gap-3">
              <Globe size={28} className="text-gold" />
              Regulatory Universe Risk Assessment
            </h1>
            <p className="text-sm text-text-tertiary mt-1">
              Theme-based risk assessment of your regulatory obligations
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Methodology Indicator */}
            <div className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 ${
              usingMethodology === 'custom' 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                : 'bg-gold/20 text-gold border border-gold/30'
            }`}>
              <ChartPie size={14} />
              {usingMethodology === 'custom' ? `Using: ${methodology?.name || 'Custom'}` : 'Using: AURA Default'}
            </div>
            
            {/* Export Buttons */}
            <button
              onClick={handleExportExcel}
              className="px-3 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg text-sm font-medium hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
            >
              <FileXls size={16} />
              Excel
            </button>
            <button
              onClick={handleExportPdf}
              className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors flex items-center gap-2"
            >
              <FilePdf size={16} />
              PDF
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-background-surface border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <ChartBar size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats.totalThemes || 0}</p>
                <p className="text-xs text-text-tertiary">Themes Assessed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-surface border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats.totalInstruments || 0}</p>
                <p className="text-xs text-text-tertiary">Instruments</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-surface border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Gauge size={20} className="text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats.avgRisk?.toFixed(1) || 0}</p>
                <p className="text-xs text-text-tertiary">Avg Risk Score</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-surface border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Warning size={20} className="text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{stats.criticalCount || 0}</p>
                <p className="text-xs text-text-tertiary">High/Critical</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search themes or instruments..."
              className="w-full pl-10 pr-4 py-2 bg-background-surface border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </div>
          
          <select
            value={filterRiskLevel}
            onChange={(e) => setFilterRiskLevel(e.target.value)}
            className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-primary focus:outline-none focus:ring-1 focus:ring-gold"
          >
            <option value="all">All Risk Levels</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          
          <button
            onClick={fetchData}
            className="px-3 py-2 bg-background-surface border border-border rounded-lg text-text-secondary hover:bg-background-hover transition-colors flex items-center gap-2"
          >
            <ArrowsClockwise size={16} />
            Refresh
          </button>
        </div>

        {/* Theme Risk Table */}
        <div className="bg-background-surface border border-border rounded-xl overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-background border-b border-border text-xs font-medium text-text-tertiary uppercase tracking-wide">
            <div className="col-span-4">Regulatory Theme</div>
            <div className="col-span-2 text-center">Impact</div>
            <div className="col-span-2 text-center">Probability</div>
            <div className="col-span-2 text-center">Risk Score</div>
            <div className="col-span-2 text-center">Risk Level</div>
          </div>

          {/* Theme Rows */}
          {filteredThemeRisks.length === 0 ? (
            <div className="p-12 text-center">
              <Globe size={48} className="mx-auto text-text-tertiary mb-3" />
              <p className="text-text-secondary">No themes with risk assessments found</p>
              <p className="text-sm text-text-tertiary mt-1">
                Publish documents from your Regulatory Library to assess theme risks
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredThemeRisks.map((theme) => {
                const riskStyle = getRiskLevelStyle(theme.risk_level);
                const isExpanded = expandedThemes.has(theme.theme_id);
                const IconComponent = ICON_MAP[theme.icon] || Globe;
                
                return (
                  <div key={theme.theme_id}>
                    {/* Theme Row */}
                    <div 
                      className={`grid grid-cols-12 gap-4 px-6 py-4 cursor-pointer hover:bg-background-hover transition-colors ${riskStyle.bg}`}
                      onClick={() => toggleThemeExpand(theme.theme_id)}
                    >
                      <div className="col-span-4 flex items-center gap-3">
                        <button className="text-text-tertiary hover:text-text-primary">
                          {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </button>
                        <div className={`w-8 h-8 rounded-lg ${riskStyle.bg} flex items-center justify-center`}>
                          <IconComponent size={16} className={riskStyle.text} />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary">{theme.theme_name}</p>
                          <p className="text-xs text-text-tertiary">{theme.instruments?.length || 0} instruments</p>
                        </div>
                      </div>
                      
                      <div className="col-span-2 flex items-center justify-center">
                        <span className="px-3 py-1 bg-background rounded-lg text-sm font-mono text-text-primary">
                          {theme.impact}
                        </span>
                      </div>
                      
                      <div className="col-span-2 flex items-center justify-center">
                        <span className="px-3 py-1 bg-background rounded-lg text-sm font-mono text-text-primary">
                          {theme.probability}
                        </span>
                      </div>
                      
                      <div className="col-span-2 flex items-center justify-center">
                        <span className={`px-3 py-1 rounded-lg text-sm font-bold ${riskStyle.text}`}>
                          {theme.risk_score}
                        </span>
                      </div>
                      
                      <div className="col-span-2 flex items-center justify-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${riskStyle.badge}`}>
                          {theme.risk_level}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTheme(theme);
                            setShowEditRiskModal(true);
                          }}
                          className="p-1 text-text-tertiary hover:text-gold transition-colors"
                          title="Edit risk assessment"
                        >
                          <PencilSimple size={14} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Instruments List (Expanded) */}
                    <AnimatePresence>
                      {isExpanded && theme.instruments?.length > 0 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden bg-background"
                        >
                          <div className="px-6 py-3 border-t border-border/50">
                            <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-3 pl-8">
                              Instruments under {theme.theme_name}
                            </p>
                            <div className="space-y-2 pl-8">
                              {theme.instruments.map((instrument, idx) => {
                                const typeConfig = INSTRUMENT_TYPES[instrument.type] || INSTRUMENT_TYPES.other;
                                return (
                                  <div 
                                    key={idx}
                                    className="flex items-center gap-3 py-2 px-4 bg-background-surface rounded-lg border border-border/50"
                                  >
                                    <FileText size={16} className="text-text-tertiary" />
                                    <span className="flex-1 text-sm text-text-primary">{instrument.name}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeConfig.color}`}>
                                      {typeConfig.label}
                                    </span>
                                    <span className="text-xs text-text-tertiary">{instrument.regulator}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Risk Modal */}
      <AnimatePresence>
        {showEditRiskModal && selectedTheme && (
          <EditThemeRiskModal
            theme={selectedTheme}
            methodology={methodology}
            usingMethodology={usingMethodology}
            onClose={() => {
              setShowEditRiskModal(false);
              setSelectedTheme(null);
            }}
            onSave={() => {
              setShowEditRiskModal(false);
              setSelectedTheme(null);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Edit Theme Risk Modal
const EditThemeRiskModal = ({ theme, methodology, usingMethodology, onClose, onSave }) => {
  const { token } = useAuth();
  const [impact, setImpact] = useState(theme.impact || 3);
  const [probability, setProbability] = useState(theme.probability || 3);
  const [rationale, setRationale] = useState(theme.rationale || '');
  const [saving, setSaving] = useState(false);

  const riskScore = impact * probability;
  
  // Determine risk level based on methodology
  const getRiskLevel = (score) => {
    if (usingMethodology === 'custom' && methodology?.extracted_data?.risk_levels) {
      for (const level of methodology.extracted_data.risk_levels) {
        const [min, max] = level.score_range || [0, 25];
        if (score >= min && score <= max) {
          return level.label;
        }
      }
    }
    // AURA default
    if (score >= 17) return 'Critical';
    if (score >= 10) return 'High';
    if (score >= 5) return 'Medium';
    return 'Low';
  };

  const riskLevel = getRiskLevel(riskScore);
  const riskStyle = getRiskLevelStyle(riskLevel);

  // Get scale labels from methodology or use AURA defaults
  const impactLabels = usingMethodology === 'custom' && methodology?.extracted_data?.impact_scale
    ? methodology.extracted_data.impact_scale
    : [
        { level: 1, label: 'Negligible' },
        { level: 2, label: 'Minor' },
        { level: 3, label: 'Moderate' },
        { level: 4, label: 'Major' },
        { level: 5, label: 'Severe' }
      ];

  const probabilityLabels = usingMethodology === 'custom' && methodology?.extracted_data?.probability_scale
    ? methodology.extracted_data.probability_scale
    : [
        { level: 1, label: 'Rare' },
        { level: 2, label: 'Unlikely' },
        { level: 3, label: 'Possible' },
        { level: 4, label: 'Likely' },
        { level: 5, label: 'Almost Certain' }
      ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/regulatory-universe/theme-risks/${theme.theme_id}`,
        {
          impact,
          probability,
          risk_score: riskScore,
          risk_level: riskLevel,
          rationale
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Theme risk assessment updated');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update risk assessment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-background-surface border border-border rounded-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-heading font-medium text-text-primary">Edit Risk Assessment</h2>
              <p className="text-sm text-text-tertiary mt-1">{theme.theme_name}</p>
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Risk Display */}
          <div className={`p-4 rounded-lg ${riskStyle.bg} border ${riskStyle.border}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Calculated Risk</span>
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-bold ${riskStyle.text}`}>{riskScore}</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${riskStyle.badge}`}>
                  {riskLevel}
                </span>
              </div>
            </div>
          </div>

          {/* Methodology Indicator */}
          <div className="p-3 bg-background rounded-lg border border-border text-xs text-text-tertiary flex items-center gap-2">
            <Info size={14} />
            Using: {usingMethodology === 'custom' ? methodology?.name || 'Custom Methodology' : 'AURA Default Methodology'}
          </div>

          {/* Impact Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-secondary">Impact</label>
              <span className="text-sm text-gold font-medium">
                {impact} - {impactLabels.find(l => l.level === impact)?.label || ''}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={impact}
              onChange={(e) => setImpact(parseInt(e.target.value))}
              className="w-full accent-gold"
            />
            <div className="flex justify-between text-xs text-text-tertiary mt-1">
              {impactLabels.map(l => (
                <span key={l.level}>{l.label}</span>
              ))}
            </div>
          </div>

          {/* Probability Slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-text-secondary">Probability</label>
              <span className="text-sm text-gold font-medium">
                {probability} - {probabilityLabels.find(l => l.level === probability)?.label || ''}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="5"
              value={probability}
              onChange={(e) => setProbability(parseInt(e.target.value))}
              className="w-full accent-gold"
            />
            <div className="flex justify-between text-xs text-text-tertiary mt-1">
              {probabilityLabels.map(l => (
                <span key={l.level}>{l.label}</span>
              ))}
            </div>
          </div>

          {/* Rationale */}
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Rationale</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain the risk assessment rationale..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-gold resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border text-text-secondary rounded-lg hover:bg-background transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-gold text-background rounded-lg font-medium hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <ArrowsClockwise size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Assessment
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UniverseRiskDashboard;
